<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="1000px"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        color="primary"
        fab
        title="Add New Branch Payment Channel"
        v-on="on"
      >
        <v-icon medium>
          mdi-plus-circle
        </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      :loading="loading"
      color="primary"
      style="margin-bottom:0"
    >
      <template v-slot:header>
        <v-card-title>
          <div class="px-2">
            <h4 class="title font-weight-light">
              Branch Payment Channel
            </h4>
            <p class="category font-weight-thin">
              Create Branch Payment Channel
            </p>
          </div>
          <v-spacer />
          <v-btn  
            text
            icon 
            color="white"
            @click="dialog = false"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
      </template>
      <v-form
        v-if="dialog && !loading"
      >
        <v-container
          class="overflow"
          data-simplebar
        >
          <v-row>
            <v-col>
              <v-autocomplete
                v-model="schemaId"
                v-validate="'required'"
                item-text="schema_name"
                item-value="id"
                :items="schemas"
                label="Schema"
                :error-messages="errors.collect('schema')"
                data-vv-name="schema"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-autocomplete
                v-model="branchId"
                v-validate="'required'"
                item-text="name"
                item-value="id"
                :items="filteredBranches"
                label="Branch"
                :error-messages="errors.collect('branch')"
                data-vv-name="branch"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-data-table
                v-model="selectedPaymentChannels"
                dense
                :headers="headers"
                :items="paymentChannels"
                hide-default-footer
                fixed-header
                disable-pagination
                item-key="id"
                item-value="id"
                show-select
                sort-by="company.name"
                class="elevation-1 mb-3"
                :search="search"
                height="335px"
              >
                <template v-slot:top>
                  <v-toolbar
                    flat
                  >
                    <v-row>
                      <v-col class="col">
                        <v-toolbar-title>
                          Select at least one Payment Channel:
                        </v-toolbar-title>
                      </v-col>
                      <v-col class="col">
                        <v-text-field
                          v-model="search"
                          label="Search"
                          append-icon="search"
                        />
                      </v-col>
                    </v-row>
                  </v-toolbar>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <span
                v-show="paymentChannelError"
                class="red--text"
              > 
                Must have at least one payment channel!
              </span>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <div v-show="selectedPaymentChannels.length > 0">
                <span> Branch Account </span>
                <v-radio-group
                  v-model="selectedBranchAccount"
                  v-validate="{
                    branchAccountTaken: selectedPaymentChannels
                  }"
                  :error-messages="errors.collect('branchAccount')"
                  data-vv-name="branchAccount"
                >
                  <v-radio
                    v-for="(paymentChannel, index) in selectedPaymentChannels"
                    :key="index"
                    :label="paymentChannel.combined"
                    :value="index"
                    :disabled="!branchAccountTaken(index, selectedPaymentChannels)"
                  />
                </v-radio-group>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field
                v-model="account_uid"
                v-validate="'required'"
                label="Account UID"
                :error-messages="errors.collect('accountUid')"
                data-vv-name="accountUid"
                data-vv-as="account uid"
              />
            </v-col>
          </v-row>
                    
          <v-card-actions>
            <v-spacer />
            <v-btn
              :loading="progressing"
              color="success"
              @click="submitForm"
            >
              Create
            </v-btn>
          </v-card-actions>
        </v-container>
      </v-form>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    inject: ['$validator'],
    data () {
      return {
        dialog: false,
        loading: false,
        paymentChannelError: false,
        progressing: false,
            
        branches: [],
        paymentChannels: [],
        schemas: [],
        search: '',

        branchId: '',
        branchAccounts: [],
        selectedPaymentChannels: [],
        selectedBranchAccount: '',
        account_uid: '',
        schemaId: '',
        headers: [
          { text: 'Company', value: 'company.name', align: 'left' },
          { text: 'Type', value: 'payment_channel_type.name', align: 'left' },
          { text: 'Bank', value: 'bank.name', align: 'left' },
          { text: 'Reference No', value: 'reference', align: 'left' },
        ],
      }
    },
    computed: {
      filteredBranches () {
        if (!this.schemaId) return
        
        return this.branches.filter(branch => {
          return branch.company_uid === this.schemaId && !(this.existedBranch(branch.id))
        })
      },
    },
    watch: {
      dialog (value) {
        if (!value) {
          this.$validator.pause()
          this.$resetData()
        } else {
          this.loadData()
          this.$validator.resume()
        }
      },
    },
    mounted () {
      const scope = this

      this.$validator.extend('branchAccountTaken', {
        getMessage (field, args) {
          return 'The payment channel has been assigned as branch payment account by other branch'
        },
        validate (value, args) {
          return scope.branchAccountTaken(value, args)
        },
      })
    },
    methods: {
      loadData () {
        this.loading = true
        Promise.all([
          this.$api.companies().withSchema(),
          this.$api.paymentChannels().list(),
          this.$api.branches().list(),
          this.$api.branchPaymentChannels().overview(),
          this.$api.branchPaymentChannels().showBranchAccount(),
        ]).then(([schemas, paymentChannels, branches, branchPaymentChannelsOverview, branchAccounts]) => {
          this.schemas = schemas.data
          paymentChannels.data.map(function (item) {
            if (item.bank) {
              item.combined = `${item.company.name} - ${item.payment_channel_type.name} (${item.bank.name}) - ${item.reference})`
            } else {
              item.combined = `${item.company.name}-${item.payment_channel_type.name}`
            }
          })
          branches.data.map(item => {
            this.branches.push({ id: item.branch_uid, name: item.branch_name, company_uid: item.company_uid })
          })
          this.branchAccounts = branchAccounts.data
          this.paymentChannels = paymentChannels.data
          this.overviewBranches = branchPaymentChannelsOverview.data
          this.loading = false
        })
      },
      existedBranch (branchId) {
        const found = this.overviewBranches.some(overviewBranch => {
          return overviewBranch.branch_uid === branchId
        })
        return found
      },
      branchAccountTaken (value, args) {
        const paymentChannelId = args[value].id
        const found = this.branchAccounts.some(branchAccount => {
          return branchAccount.payment_channel_id === paymentChannelId
        })
        return !found
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            if (this.selectedPaymentChannels.length > 0) {
              this.progressing = true
              this.$api.branchPaymentChannels().create(this.prepareFormData())
                .then((response) => { 
                  if (response.status === 201) {
                    this.$emit('created')
                    this.$snack({ color: 'success', message: 'Branch Payment Channel created successfully' })

                    this.dialog = false
                  }
                }).catch(err => {
                  this.$snack({ color: 'error', message: err.response.statusText })
                }).then(() => {
                  this.progressing = false
                })
            } else { this.paymentChannelError = true }
          })
      },
      prepareFormData () {
        const paymentChannels = []
        this.selectedPaymentChannels.map((paymentChannel, index) => {
          let accountUid = ''
          if (index === this.selectedBranchAccount) {
            accountUid = this.account_uid
          } else {
            accountUid = null
          }
          const tempPaymentChannel = {
            payment_channel_id: paymentChannel.id,
            acct_uid: accountUid,
          }
          paymentChannels.push(tempPaymentChannel)
        })
        return {
          branch_uid: this.branchId,
          payment_channels: paymentChannels,
        }
      },
    },
  }
</script>
<style>
  .overflow {
    height: 60vh;
    overflow-x: hidden;
  }

  .simplebar-track.simplebar-vertical .simplebar-scrollbar:before {
       background-image: linear-gradient( grey , grey);
  }
  
</style>
